import React from 'react';
import classNames from 'classnames';

import BlogPostGrid from '../blogPostGrid/BlogPostGrid';
import CTAButton from '../ctaButton/CTAButton';
import PropTypes from 'prop-types';
import { slugify } from '../../utils';
import useBlogPosts from '../../hooks/useBlogPosts';

const getFilteredPosts = (blogPosts, selectedPosts, category, tag) => {
  let filteredPosts = [];

  if (selectedPosts) {
    // Check if supplied posts exist
    selectedPosts.forEach((slug) => {
      const postExists = blogPosts.some((blogPost) => {
        return slug === blogPost.slug;
      });
      if (!postExists) {
        throw new Error(`Selected post "${slug}" does not exist, please check the post slug is correct`);
      }
    });

    // Get post data by supplied post slugs
    const unsortedFilteredPosts = blogPosts.filter((blogPost) => {
      return selectedPosts.includes(blogPost.slug);
    });

    // Sort filtered posts by order supplied
    filteredPosts = Array.from(unsortedFilteredPosts).sort(
      (a, b) => selectedPosts.indexOf(a.slug) - selectedPosts.indexOf(b.slug)
    );
  } else if (category) {
    filteredPosts = blogPosts.filter((blogPost) => {
      const categories = blogPost.Categories.map((cat) => cat.toLowerCase());
      return categories.includes(category.toLowerCase());
    });
  } else if (tag) {
    filteredPosts = blogPosts.filter((blogPost) => {
      if (blogPost.Tags) {
        const tags = blogPost.Tags.map((tag) => tag.toString().toLowerCase());
        return tags.includes(tag.toLowerCase());
      }
      return false;
    });
  }

  return filteredPosts;
};

const getLatestPosts = (filteredPosts, numberOfPosts) => {
  const posts = filteredPosts;
  if (numberOfPosts) {
    const postLimit = numberOfPosts <= 3 ? 3 : 6;
    return posts.slice(0, postLimit);
  }
  return posts;
};

const FilteredBlogPostGrid = ({ title, titleSize, selectedPosts, category, tag, numberOfPosts, customPadding }) => {
  const { blogPosts } = useBlogPosts();
  const filteredPosts = getFilteredPosts(blogPosts, selectedPosts, category, tag);
  const latestPosts = getLatestPosts(filteredPosts, numberOfPosts);

  const componentClasses = classNames('blog-post-category-grid', {
    'blog-post-category-grid--no-padding-top': customPadding === 'no-top',
    'blog-post-category-grid--no-padding-bottom': customPadding === 'no-bottom',
    'blog-post-category-grid--no-padding-top-bottom': customPadding === 'no-padding'
  });

  const titleClasses = classNames('blog-post-category-grid__title', {
    'blog-post-category-grid__title--small': titleSize === 'small'
  });

  return (
    <section className={componentClasses}>
      <h2 className={titleClasses}>{title}</h2>
      <BlogPostGrid gridStyle="blog-grid-categories" blogPosts={latestPosts} />
      {category || tag ? (
        <CTAButton
          text="Read more blog posts"
          className="cta-button--secondary"
          link={category ? '/blog/category/' + slugify(category) + '/' : tag ? '/blog/tag/' + slugify(tag) + '/' : null}
        />
      ) : null}
    </section>
  );
};

FilteredBlogPostGrid.propTypes = {
  selectedPosts: PropTypes.arrayOf(PropTypes.string),
  category: PropTypes.oneOf([
    'Personal finance',
    'Business',
    'Product news',
    'Engineering',
    'Team Starling',
    'Banking Services'
  ]),
  numberOfPosts: PropTypes.oneOf(['3', '6']),
  tag: PropTypes.string,
  title: PropTypes.string.isRequired,
  titleSize: PropTypes.string,
  customPadding: PropTypes.oneOf(['no-top', 'no-bottom', 'no-padding'])
};

export default FilteredBlogPostGrid;
